<template>
  <div v-if="isAccountEnabled">
    <label class="text-gpx_xs text-gpx_gray-200 font-medium uppercase">
      Link for this tracker
    </label>
    <!-- The link field stays in place across states so the popup layout does
    not shift. When the tracker is not shared there is no uuid, so it is greyed
    out with a placeholder and its Copy button disabled. -->
    <VInputCopy
      :modelValue="shareUrl"
      :disabled="!isActive"
      placeholder="Enable the link to generate a URL"
      aria-label="Public link"
      type="text"
      size="sm"
      class="mb-2 mt-1"
      :class="{ 'opacity-60': !isActive }"
    />
    <div class="flex items-center justify-between gap-x-2">
      <span class="text-gpx_sm text-gpx_gray-200">
        {{
          isActive ? 'Live · full history since activation' : 'Not shared'
        }}
      </span>
      <Button
        v-if="$can('update', 'Devices')"
        size="sm"
        variant="secondary"
        :loading="isSaving"
        @click="toggleSharing"
      >
        {{ toggleLabel }}
      </Button>
    </div>
  </div>
  <p v-else class="text-gpx_sm text-gpx_gray-200">
    Public tracker links are off for this account, so this tracker has no public
    link. Only an Administrator can turn them on, in Manage → Tracker Sharing.
  </p>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { updateDevice } from '@gpx-spa/queries';
import VInputCopy from '@/components/ui/VInputCopy.vue';
import { COMMON_UPDATE_ERROR, COMMON_UPDATE_SUCCESS } from '@/config/constants';

const props = defineProps({
  device: {
    type: Object,
    default: null,
  },
  deviceId: {
    type: Number,
    required: true,
  },
});

const store = useStore();
const toast = useToast();
const { mutate: mutateSharing, isPending: isSaving } = updateDevice();

const accountSettings = computed(() => store.getters['auth/accountSettings']);
const isAccountEnabled = computed(() =>
  store.getters['auth/accountSettingIsOn']('tracker_sharing_enabled'),
);
const defaultIsPublic = computed(
  () => accountSettings.value.tracker_sharing_default === 'public',
);

// Resolved server-side: the account gate overrides an explicit Public.
const isActive = computed(() => !!props.device?.tracker_sharing_active);
// The uuid is only sent while the link is live. Built here rather than returned
// by the engine, as shipments and reports already do.
const shareUrl = computed(() => {
  const uuid = props.device?.tracker_sharing_uuid;
  return uuid ? `${window.location.origin}/trackers/track/${uuid}` : '';
});

// One button toggles the device between an explicit override and 'inherit',
// with the pair chosen by the account default. When the default shows links,
// the meaningful choice is hide (private) ⇄ inherit; when it hides them, it is
// show (public) ⇄ inherit. Whichever value currently yields the active state is
// the one we toggle away from.
const toggleTarget = computed(() => {
  if (defaultIsPublic.value) {
    return isActive.value ? 'private' : 'inherit';
  }
  return isActive.value ? 'inherit' : 'public';
});
const toggleLabel = computed(() =>
  isActive.value ? 'Disable link' : 'Enable link',
);

const toggleSharing = () => {
  mutateSharing(
    {
      deviceId: props.deviceId,
      payload: { tracker_sharing: toggleTarget.value },
    },
    {
      onSuccess: () => {
        toast.success(COMMON_UPDATE_SUCCESS);
      },
      onError: () => {
        toast.error(COMMON_UPDATE_ERROR);
      },
    },
  );
};
</script>
